<template>
  <Flex
    direction="column"
    align="center"
    :class="[$style.base, { [$style.smallScreen]: $screen !== 'l' }]"
  >
    <BaseHeading size="s">
      {{ $t(headingKey) }}
    </BaseHeading>
    <BaseCheckbox
      v-if="props.medical && props.mode !== 'receipt'"
      v-model="medicalOverride"
      :label="$t('admin.invoices_and_receipts.invoice_settings.medical_preview_toggle')"
      v-test="'admin-invoice-preview-medical-toggle'"
    />
    <div
      ref="wrapperEl"
      :class="$style.iframeWrapper"
      :style="wrapperStyle"
    >
      <iframe
        v-show="isLoaded"
        ref="iframeEl"
        :src="iframeSrc"
        :class="$style.iframe"
        :style="iframeScaleStyle"
        v-test="'admin-invoice-preview-iframe'"
      />
      <div
        v-if="!isLoaded"
        :class="$style.loadingOverlay"
      >
        <BaseSpinner />
      </div>
    </div>
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { useDebounceFn, useElementSize } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import config from '@/config';
import type { InvoiceFormData } from './useInvoices';

const props = defineProps<{
  formData: InvoiceFormData;
  mode?: 'invoice' | 'receipt';
  medical?: boolean;
}>();

const NATURAL_WIDTHS = {
  invoice: 794, // A4 width in pixels
  receipt: 302, // 80mm thermal paper width in pixels
};

// The iframe is border-box and carries $spacing * 2 of padding on every side, so that
// padding is subtracted from the document viewport rather than added around it. Any
// height we set has to include it, or the document gets a viewport shorter than its
// content while the wrapper still reserves the full height — a blank tail.
const IFRAME_INSET = 16 * 2 * 2;

// Slack below the last page of an A4 invoice. Receipt paper is cut to length, so it
// gets none.
const EXTRA_BOTTOM_SPACE = {
  invoice: 32,
  receipt: 0,
};

const naturalWidth = computed(() => NATURAL_WIDTHS[props.mode ?? 'invoice']);

const headingKey = computed(() =>
  props.mode === 'receipt'
    ? 'admin.invoices_and_receipts.receipt_settings.preview_title'
    : 'admin.invoices_and_receipts.invoice_settings.preview_title',
);

const wrapperEl = ref<HTMLDivElement | null>(null);
const { width: containerWidth } = useElementSize(wrapperEl);
const iframeEl = ref<HTMLIFrameElement | null>(null);
const iframeNaturalHeight = ref(0);
const isLoaded = ref(false);
const iframeSrc = ref('');
const medicalOverride = ref(false);

// Tags each iframe navigation so a late-arriving height message from a stale, already
// replaced document (the contentWindow is reused across same-element navigations, so it
// can't be used to tell documents apart) can't overwrite the height of the current one.
const latestRequestId = ref(0);

const buildSrc = (requestId: number) => {
  const params = new URLSearchParams();
  params.set('request_id', String(requestId));
  params.set('invoice_header', props.formData.invoiceHeader ?? '');
  params.set('invoice_footer', props.formData.invoiceFooter ?? '');
  params.set(
    'invoice_footer_thermal',
    props.formData.invoiceFooterThermal ?? '',
  );
  params.set('receipt_header', props.formData.receiptHeader ?? '');
  params.set(
    'invoice_recipient_template',
    props.formData.invoiceRecipientTemplate ?? '',
  );
  params.set(
    'show_vat_on_invoice',
    String(props.formData.settings.sales.showVatOnInvoice ?? false),
  );
  params.set(
    'show_treatment_date_on_invoice',
    String(props.formData.settings.sales.showTreatmentDateOnInvoice ?? false),
  );
  params.set(
    'invoice_due_date_in_days',
    String(props.formData.settings.sales.invoiceDueDateInDays),
  );
  if (props.mode === 'receipt') params.set('receipt', 'true');
  if (props.medical) {
    params.set('medical', String(medicalOverride.value));
  }
  return `${config.backendUrl}/orders/admin_preview/preview?${params.toString()}`;
};

const resetLoading = () => {
  isLoaded.value = false;
  iframeNaturalHeight.value = 0;
};

const rebuildPreview = () => {
  latestRequestId.value += 1;
  iframeSrc.value = buildSrc(latestRequestId.value);
};

const debouncedUpdate = useDebounceFn(() => {
  rebuildPreview();
}, 700);

watch(
  () => props.mode,
  () => {
    resetLoading();
    rebuildPreview();
  },
);

watch(medicalOverride, () => {
  resetLoading();
  rebuildPreview();
});

watch(
  () => props.formData,
  () => {
    resetLoading();
    debouncedUpdate();
  },
  { deep: true },
);

const scale = computed(() =>
  containerWidth.value > 0
    ? Math.min(1, containerWidth.value / naturalWidth.value)
    : 1,
);

const iframeScaleStyle = computed(() => {
  const left = Math.max(
    0,
    (containerWidth.value - naturalWidth.value * scale.value) / 2,
  );

  return {
    width: `${naturalWidth.value}px`,
    left: `${left}px`,
    transformOrigin: 'top left',
    transform: `scale(${scale.value})`,
  };
});

const wrapperStyle = computed(() => {
  const style: Record<string, string> = {};
  if (iframeNaturalHeight.value) {
    style.height = `${(iframeNaturalHeight.value + 2) * scale.value}px`;
  }
  if (props.mode === 'receipt') {
    style.maxWidth = `${naturalWidth.value}px`;
  }
  return style;
});

const handleMessage = (event: MessageEvent) => {
  if (event.source !== iframeEl.value?.contentWindow) {
    return;
  }

  // The iframe's contentWindow is the same WindowProxy across same-element navigations,
  // so a message from a document we've already replaced can still arrive after this
  // check. Only trust messages tagged with the request that's currently loading.
  if (event.data?.requestId !== String(latestRequestId.value)) {
    return;
  }

  if (event.data?.iframeHeight && iframeEl.value) {
    const naturalHeight =
      event.data.iframeHeight +
      IFRAME_INSET +
      EXTRA_BOTTOM_SPACE[props.mode ?? 'invoice'];
    iframeNaturalHeight.value = naturalHeight;
    iframeEl.value.style.height = `${naturalHeight}px`;
    isLoaded.value = true;
  }
};

onMounted(() => {
  rebuildPreview();
  window.addEventListener('message', handleMessage);
});

onUnmounted(() => window.removeEventListener('message', handleMessage));
</script>

<style
  lang="scss"
  module
>
.base {
  width: 100%;
  height: 100%;
  &.smallScreen {
    padding-bottom: 0;
  }
  &:not(.smallScreen) {
    padding-bottom: $spacing * 2;
  }
}
.iframeWrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #fff;
  border: 1px solid var(--color-border);
}
.iframe {
  position: absolute;
  top: 0;
  padding: $spacing * 2;
  border: none;
}
.loadingOverlay {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing * 4;
}
</style>
